import { useState, useEffect } from 'react';
import { useDebounce } from '@hooks';
import { showError } from '@utils';
import { usersServices } from '@services';
import { getUsersFilterPayload } from '@helpers';
import { formatUserOption } from 'helpers/sidebarGenericTicket';

export const useUserSearch = (selectedSubscription) => {
  const [usersOptions, setUsersOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const debouncedSearchInput = useDebounce(searchTerm, 500);

  const fetchUsers = async (searchValue) => {
    if (selectedSubscription) {
      const subscribers = selectedSubscription.subscribers || [];
      const filteredUsers = subscribers
        .filter((user) => {
          if (!searchValue || !searchValue.trim()) return true;
          const searchLower = searchValue.toLowerCase();
          return (
            user.email?.toLowerCase().includes(searchLower) ||
            user.firstName?.toLowerCase().includes(searchLower) ||
            user.lastName?.toLowerCase().includes(searchLower) ||
            user.phoneNumber?.includes(searchValue) ||
            user.internalId?.toLowerCase().includes(searchLower)
          );
        })
        .map((user) => {
          const matchingSubscription = user.subscriptions?.find(
            (subscription) => subscription.id === selectedSubscription.id
          );
          return {
            ...formatUserOption(user),
            activeDriver: matchingSubscription?.activeDriver,
          };
        });
      setUsersOptions(filteredUsers);
      return;
    }

    if (!searchValue || !searchValue.trim()) {
      setUsersOptions([]);
      return;
    }

    setIsLoadingUsers(true);
    try {
      const filterPayload = getUsersFilterPayload(searchValue);
      const response = await usersServices.filterUsers({
        params: { query: searchValue },
        payload: filterPayload,
      });
      const usersResponse = response?.data || [];
      const normalizedUsers = usersResponse.map(formatUserOption);
      setUsersOptions(normalizedUsers);
    } catch (error) {
      showError(error);
      setUsersOptions([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsers(debouncedSearchInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput, selectedSubscription]);

  const handleInputChange = (newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
      setSearchTerm(newInputValue || '');
      setIsAutocompleteOpen(!!newInputValue && newInputValue.length > 0);
    }
  };

  const handleAutocompleteClose = () => {
    setIsAutocompleteOpen(false);
    setSearchTerm('');
    if (!selectedSubscription) {
      setUsersOptions([]);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setUsersOptions([]);
  };

  return {
    usersOptions,
    searchTerm,
    isLoadingUsers,
    isAutocompleteOpen,
    debouncedSearchInput,
    setIsAutocompleteOpen,
    handleInputChange,
    handleAutocompleteClose,
    fetchUsers,
    clearSearch,
    setSearchTerm,
  };
};
